import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, ArrowRight, Users, Gift, Flag } from "lucide-react";
import SEO from "@/components/SEO";
import FreeTipCard from "@/components/FreeTipCard";
import SlotBanner from "@/components/SlotBanner";

export default function FreeTips() {
  return (
    <div data-testid="free-tips-page">
      <SEO
        title="Free Daily Football Tip — Fresh Pick Every Day"
        description="One free football tip posted every single day. Solid picks from reliable amateur tipsters — the perfect way to sample our platform before joining the professional Asian Handicap subscription."
        path="/free-tips"
        keywords="free football tips, daily football tip, free soccer picks, free predictions, amateur tipsters, free AH tips"
      />

      {/* HERO */}
      <section className="bg-ink-950 text-white py-14 sm:py-20 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] rounded-full bg-emerald-700/25 blur-[120px]" />
        <div className="absolute -bottom-20 -left-20 w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] rounded-full bg-gold-500/15 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-500/40 bg-gold-500/10 text-gold-300 text-[10px] sm:text-xs font-mono uppercase tracking-widest mb-4">
            <Gift className="w-3.5 h-3.5" />
            One Free Tip · Every Day
          </div>
          <h1
            data-testid="free-tips-title"
            className="font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight max-w-3xl"
          >
            A <span className="gold-text">free football tip</span> in your feed every single day.
          </h1>
          <p className="mt-4 sm:mt-5 max-w-2xl text-white/70 text-sm sm:text-base lg:text-lg leading-relaxed">
            Every day, we publish one hand-picked free football tip on the site — no signup, no payment, no email required.
            It's the easiest way to see how we work before deciding whether the paid Asian Handicap subscription is for you.
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-14 sm:py-20 bg-ink-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Free tip explainer block */}
          <article
            data-testid="free-tip-section"
            className="mt-6 sm:mt-8 p-6 sm:p-10 rounded-2xl bg-white border border-ink-200 shadow-sm"
          >
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-700/10 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <h2 className="font-display font-bold text-2xl sm:text-3xl text-ink-900">
                  How the free daily tip works
                </h2>
                <p className="text-sm text-ink-500 mt-1">
                  Fresh selection posted here every day of the week.
                </p>
              </div>
            </div>

            <div className="space-y-5 text-ink-700 leading-relaxed text-[15px] sm:text-base">
              <p>
                We know that trust in this space has to be <em>earned</em>. That's why, on top of the private
                subscription, we publish one <strong>free football tip every day</strong> right here on the site.
                Come back tomorrow, and there'll be a new one waiting. No account, no paywall — just a single,
                honest pick you can follow (or track) at zero cost.
              </p>

              <p>
                The free daily tip lives on this page. It's typically posted early enough for you to grab a
                fair line at your bookmaker, and it will always tell you the match, the market, the price and
                the reasoning in one line. Simple as that.
              </p>
            </div>

            {/* Distinction block */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                data-testid="free-vs-paid-free"
                className="p-5 sm:p-6 rounded-xl border border-ink-200 bg-ink-50/50"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Flag className="w-4 h-4 text-ink-500" />
                  <div className="text-[11px] font-mono uppercase tracking-widest text-ink-500">
                    Free Daily Tip
                  </div>
                </div>
                <h3 className="font-display font-bold text-lg text-ink-900 mb-2">
                  Solid amateur picks
                </h3>
                <p className="text-sm text-ink-700 leading-relaxed">
                  Free tips come from a network of <strong>amateur tipsters we trust</strong> — reliable, disciplined bettors who share their best call
                  of the day. They're not the professionals behind the subscription, but they are consistent and honest.
                  We publish these openly so more people can find the site and get to know us. It's the traffic engine
                  of Asian Picks — nothing more, nothing less.
                </p>
              </div>

              <div
                data-testid="free-vs-paid-sub"
                className="p-5 sm:p-6 rounded-xl border-2 border-emerald-700/30 bg-emerald-700/[0.04] relative"
              >
                <div className="absolute -top-3 left-5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-widest">
                  <ShieldCheck className="w-3 h-3" strokeWidth={3} />
                  Subscription
                </div>
                <div className="flex items-center gap-2 mb-3 mt-1">
                  <Users className="w-4 h-4 text-emerald-700" />
                  <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-700">
                    Paid Archive
                  </div>
                </div>
                <h3 className="font-display font-bold text-lg text-ink-900 mb-2">
                  Professional AH tipsters
                </h3>
                <p className="text-sm text-ink-700 leading-relaxed">
                  Every match inside the private <Link to="/predictions" className="text-emerald-700 underline hover:text-gold-600">Predictions Archive</Link> comes from
                  <strong> professional Asian Handicap tipsters</strong> with years of documented performance. This is our
                  premium content: deep research, disciplined staking, tight odds, and the 81.1% verified win rate you see on the homepage.
                  It's a completely different tier from the free daily tip — and it's what our subscribers pay for.
                </p>
              </div>
            </div>

            <div className="mt-8 p-5 rounded-xl bg-gold-500/[0.08] border border-gold-500/30 text-ink-900 text-sm leading-relaxed">
              <strong className="font-semibold">In one sentence:</strong>{" "}
              the free daily tip is a friendly taste; the subscription is the real, professional Asian Handicap product.
              Enjoy the free one — and when you're ready for the sharp, verified stuff,
              <Link to="/pricing" className="ml-1 font-semibold text-emerald-700 underline hover:text-gold-600">
                see our subscription plans
              </Link>.
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/predictions"
                data-testid="free-tips-cta-archive"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-ink-300 hover:border-emerald-700 hover:text-emerald-700 text-ink-700 font-semibold text-sm transition-all"
              >
                See the subscribers' archive
              </Link>
              <Link
                to="/pricing"
                data-testid="free-tips-cta-plans"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm transition-all shadow-lg shadow-emerald-700/20"
              >
                View subscription plans
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </article>

          <SlotBanner slot="free_tips_above_tip" testId="free-tips-above-tip-slot-banner" />

          {/* Free Tip of the Day */}
          <div className="mt-4 sm:mt-6">
            <FreeTipCard />
          </div>

          <SlotBanner slot="free_tips_below_tip" testId="free-tips-below-tip-slot-banner" />

          {/* Small note */}
          <p className="mt-6 text-center text-xs text-ink-500 font-mono">
            18+ · Please gamble responsibly · Past performance does not guarantee future results.
          </p>
        </div>
      </section>
    </div>
  );
}
